/**
 * Shopify Cart API utilities
 * @module shared-cart/cart/api
 */

import {
  fetchConfig,
  getSectionsToRender,
  getSectionsUrl,
  renderSections,
  showCartSpinner,
} from './sections.js';

interface DiscountApplication {
  title?: string;
}

interface DiscountAllocation {
  discount_application?: DiscountApplication;
}

interface DiscountCode {
  code: string;
  applicable?: boolean;
}

export interface CartItem {
  sku?: string | number;
  quantity?: number;
  properties?: Record<string, unknown> | null;
  variant_id?: string | number;
  discount_allocations?: DiscountAllocation[];
  [key: string]: unknown;
}

export interface CartState {
  items: CartItem[];
  item_count?: number | null;
  sections?: Record<string, string | undefined>;
  errors?: unknown;
  discount_codes?: DiscountCode[];
  cart_level_discount_applications?: DiscountApplication[];
  [key: string]: unknown;
}

export interface PreparedItem {
  sku?: string;
  id: string | number;
  quantity: string | number;
  gwp?: boolean | string;
  selling_plan_id?: number | null;
  properties?: Record<string, unknown>;
}

interface PreparedCartItem {
  id: number;
  quantity: number;
  properties?: Record<string, unknown>;
  selling_plan_id?: number;
}

interface SectionPayload {
  sections?: string[];
  sections_url?: string;
}

interface RenderOptions {
  render?: boolean;
}

/**
 * Fetches the current cart state
 * @returns {Promise<Object>} The cart object
 */
export async function getCart(): Promise<CartState> {
  const response = await fetch(window.Shopify!.routes.root + 'cart.js');
  return response.json() as Promise<CartState>;
}

/**
 * Clears all items from the cart
 * @returns {Promise<Object>} The empty cart state
 */
export async function clearCart(): Promise<CartState> {
  const response = await fetch(window.Shopify!.routes.root + 'cart/clear.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json() as Promise<CartState>;
}

/**
 * Gets variant IDs of all items currently in cart
 * @returns {Promise<number[]>} Array of variant IDs
 */
export async function getCartItemsVariantIds() {
  try {
    const cart = await getCart();
    return cart.items.map((item) => Number(item.variant_id));
  } catch (error) {
    console.error('[Shared Cart] Error fetching cart variant IDs:', error);
    throw error;
  }
}

/**
 * @typedef {Object} PreparedItem
 * @property {string} [sku] - Product SKU (caller-side only; never forwarded)
 * @property {number} id - Variant ID
 * @property {number} quantity - Quantity to add
 * @property {boolean} [gwp] - Whether this is a gift with purchase item
 * @property {number|null} [selling_plan_id] - Subscription selling-plan id;
 *   forwarded only when not null/undefined (Shopify rejects null).
 * @property {Object<string, *>} [properties] - Line-item properties merged
 *   into the POSTed `properties` object (combined with `_gwp` when `gwp`).
 */

/**
 * Adds items to the cart
 * @param {PreparedItem[]} items - Items to add to cart
 * @param {Object} [options]
 * @param {boolean} [options.render=true] - Whether to render sections and update shipping bar
 * @returns {Promise<Object>} The updated cart state
 */
export async function addToCart(
  items: PreparedItem[],
  { render = true }: RenderOptions = {},
): Promise<CartState | null> {
  if (!items.length) {
    return null;
  }

  showCartSpinner();

  const itemsPrepared = items.map((item): PreparedCartItem => {
    const prepared: PreparedCartItem = {
      id: Number(item.id),
      quantity: Number(item.quantity),
    };
    const props = { ...item.properties };
    if (item?.gwp === true || item?.gwp === 'true') {
      props._gwp = 'true';
    }
    if (Object.keys(props).length) {
      prepared.properties = props;
    }
    if (item.selling_plan_id !== null && item.selling_plan_id !== undefined) {
      prepared.selling_plan_id = item.selling_plan_id;
    }
    return prepared;
  });

  const payload: { items: PreparedCartItem[] } & SectionPayload = { items: itemsPrepared };

  if (render) {
    payload.sections = getSectionsToRender().map((section) => section.section);
    payload.sections_url = getSectionsUrl();
  }

  const body = JSON.stringify(payload);

  console.info('[Shared Cart] Adding items to cart:', itemsPrepared);

  try {
    const response = await fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body,
    });

    const state = await response.text();
    const parsedState = JSON.parse(state) as CartState;

    if (parsedState.errors) {
      console.warn('[Shared Cart] Cart add returned errors:', parsedState.errors);
    }

    if (render) {
      if (window.updateFreeShippingBar) {
        window.updateFreeShippingBar();
      }

      renderSections(parsedState);
    }

    return parsedState;
  } catch (error) {
    console.error('[Shared Cart] Error adding items to cart:', error);
    throw error;
  }
}

/**
 * Removes an item from the cart by setting its quantity to 0
 * @param {string|number} id - The Variant ID or Line Item Key
 * @returns {Promise<Object|null>} The updated cart state or null on error
 */
export async function removeCartItem(id: string | number): Promise<CartState | null> {
  showCartSpinner();

  try {
    const response = await fetch(window.Shopify!.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: String(id),
        quantity: 0,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove item');
    }

    const updatedCart = (await response.json()) as CartState;
    console.info('[Shared Cart] Item removed from cart:', { id });
    return updatedCart;
  } catch (error) {
    console.error('[Shared Cart] Error removing cart item:', error);
    return null;
  }
}

/**
 * Applies a discount code to the cart
 * @param {string} code - Comma-separated discount codes
 * @param {Object} [options]
 * @param {boolean} [options.render=true] - Whether to render sections and update shipping bar
 * @returns {Promise<Object>} The updated cart state
 */
export async function applyDiscountCode(
  code: string,
  { render = true }: RenderOptions = {},
): Promise<CartState> {
  showCartSpinner();

  const payload: { discount: string } & SectionPayload = { discount: code };

  if (render) {
    payload.sections = getSectionsToRender().map((section) => section.section);
    payload.sections_url = getSectionsUrl();
  }

  const body = JSON.stringify(payload);

  const updateResponse = await fetch(window.Shopify!.routes.root + 'cart/update.js', {
    ...fetchConfig(),
    body,
  });

  if (!updateResponse.ok) {
    throw new Error('Failed to apply discount code');
  }

  const state = await updateResponse.text();
  let parsedState = JSON.parse(state) as CartState;

  // Shopify accepts the update (HTTP 200) even for a code it can't honour here,
  // keeping it on the cart flagged `applicable: false` — e.g. a coupon handed
  // off from another brand that maps to an automatic (non-coupon) discount on
  // this store. Left in place it renders a bogus 'applied' badge. Strip any such
  // dead code by re-applying only the live subset, so cart state matches the UI.
  const liveCodes = getLiveDiscountCodes(parsedState);
  const hasDeadCode = (parsedState.discount_codes || []).length > liveCodes.length;

  if (hasDeadCode) {
    const stripPayload = { discount: liveCodes.join(',') } as SectionPayload & {
      discount: string;
    };

    if (render) {
      stripPayload.sections = getSectionsToRender().map((section) => section.section);
      stripPayload.sections_url = getSectionsUrl();
    }

    const stripResponse = await fetch(window.Shopify!.routes.root + 'cart/update.js', {
      ...fetchConfig(),
      body: JSON.stringify(stripPayload),
    });

    if (stripResponse.ok) {
      parsedState = JSON.parse(await stripResponse.text()) as CartState;
      console.info('[Shared Cart] Removed non-applicable discount codes', {
        kept: liveCodes,
      });
    }
  }

  if (render) {
    if (window.updateFreeShippingBar) {
      window.updateFreeShippingBar();
    }

    renderSections(parsedState);
  }

  console.info('[Shared Cart] Discount code applied:', { code });
  return parsedState;
}

/**
 * Updates quantities for items already in the cart
 * @param {PreparedItem[]} items - Items with updated quantities
 * @param {Object} [options]
 * @param {boolean} [options.render=true] - Whether to render sections and update shipping bar
 * @returns {Promise<Object|null>} The updated cart state or null if no items
 */
export async function updateCartItemQuantities(
  items: PreparedItem[],
  { render = true }: RenderOptions = {},
): Promise<CartState | null> {
  if (!items.length) {
    return null;
  }

  showCartSpinner();

  const updates: Record<string, number> = {};
  for (const item of items) {
    updates[String(item.id)] = Number(item.quantity);
  }

  const payload: { updates: Record<string, number> } & SectionPayload = { updates };

  if (render) {
    payload.sections = getSectionsToRender().map((section) => section.section);
    payload.sections_url = getSectionsUrl();
  }

  const body = JSON.stringify(payload);

  console.info('[Shared Cart] Updating cart item quantities:', updates);

  try {
    const response = await fetch(window.Shopify!.routes.root + 'cart/update.js', {
      ...fetchConfig(),
      body,
    });

    const state = await response.text();
    const parsedState = JSON.parse(state) as CartState;

    if (render) {
      if (window.updateFreeShippingBar) {
        window.updateFreeShippingBar();
      }

      renderSections(parsedState);
    }

    return parsedState;
  } catch (error) {
    console.error('[Shared Cart] Error updating cart item quantities:', error);
    throw error;
  }
}

/**
 * Fetches the current cart state with fresh section HTML and renders the UI.
 * Use after a batch of cart mutations to do a single authoritative render.
 * @returns {Promise<Object>} The current cart state
 */
export async function refreshCartUI(): Promise<CartState> {
  const body = JSON.stringify({
    sections: getSectionsToRender().map((section) => section.section),
    sections_url: getSectionsUrl(),
  });

  const response = await fetch(window.Shopify!.routes.root + 'cart/update.js', {
    ...fetchConfig(),
    body,
  });

  const state = await response.text();
  const parsedState = JSON.parse(state) as CartState;

  renderSections(parsedState);

  if (window.updateFreeShippingBar) {
    window.updateFreeShippingBar();
  }

  return parsedState;
}

/**
 * Determines which of a cart's discount codes are actually 'live' — i.e. worth
 * showing to the shopper. Shopify keeps a code in `cart.discount_codes` even
 * when it grants nothing (e.g. a coupon code handed off from another brand that
 * isn't configured here), flagging it `applicable: false`. Such dead codes must
 * not surface in the cart UI.
 *
 * A code is live when either:
 *   1. Shopify marks it `applicable: true`, or
 *   2. It actually discounts the cart despite `applicable: false`. Product-level
 *      Shopify Functions discounts report `applicable: false` while still
 *      allocating line-item discounts, so we must not drop a code purely on the
 *      `applicable` flag — we confirm no discount application references it.
 *
 * @param {Object} cart - Parsed Shopify cart object
 * @returns {string[]} Codes safe to keep on the cart / show in the UI
 */
export function getLiveDiscountCodes(cart: unknown): string[] {
  // NOTE: This is the single source of truth for JS. It is exposed on
  // `window` by shared-cart/index.ts so assets/cart.js can call it without
  // bundling. The equivalent server-side logic is duplicated in:
  //   - snippets/cart-drawer.liquid → 'discounting_titles' block
  // Keep both in sync when changing the filtering rules.
  //
  // Titles of every discount application that produced a real reduction, from
  // both cart-level applications and per-line-item allocations. Shopify labels
  // these with the discount's title, which matches the entered code.
  const discountingTitles = new Set();

  for (const app of (cart as CartState | null | undefined)?.cart_level_discount_applications ||
    []) {
    if (app.title) discountingTitles.add(app.title.toUpperCase());
  }
  for (const item of (cart as CartState | null | undefined)?.items || []) {
    for (const allocation of item.discount_allocations || []) {
      const title = allocation.discount_application?.title;
      if (title) discountingTitles.add(title.toUpperCase());
    }
  }

  return ((cart as CartState | null | undefined)?.discount_codes || [])
    .filter((entry) => entry.applicable || discountingTitles.has(entry.code?.toUpperCase()))
    .map((entry) => entry.code);
}

/**
 * Gets active discount codes from the cart
 * @returns {Promise<string[]>} Array of active discount code strings
 */
export async function getActiveCartDiscounts() {
  try {
    const cart = await getCart();
    return cart?.discount_codes?.filter((x) => x.applicable)?.map((x) => x.code) || [];
  } catch (error) {
    console.error('[Shared Cart] Error fetching active discounts:', error);
    return [];
  }
}
